import React from 'react';
import { CheckCircle2, XCircle, AlertCircle } from 'lucide-react';

const PasswordStrengthIndicator = ({ password }) => {
  // Check individual requirements
  const checks = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
    special: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)
  };

  const passedChecks = Object.values(checks).filter(Boolean).length;
  const totalChecks = Object.keys(checks).length;

  // Calculate strength
  let strength = 'weak';
  let strengthColor = 'bg-red-500';
  let strengthText = 'Debole';
  let strengthTextColor = 'text-red-600';

  if (passedChecks === totalChecks) {
    strength = 'strong';
    strengthColor = 'bg-emerald-500';
    strengthText = 'Forte';
    strengthTextColor = 'text-emerald-600';
  } else if (passedChecks >= 3) {
    strength = 'medium';
    strengthColor = 'bg-amber-500';
    strengthText = 'Media';
    strengthTextColor = 'text-amber-600';
  }

  const strengthPercentage = (passedChecks / totalChecks) * 100;

  if (!password) return null;

  return (
    <div className="space-y-3 mt-2">
      {/* Strength Bar */}
      <div className="space-y-1">
        <div className="flex items-center justify-between text-xs">
          <span className="text-slate-600">Forza password:</span>
          <span className={`font-semibold ${strengthTextColor}`}>{strengthText}</span>
        </div>
        <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
          <div
            className={`h-full ${strengthColor} transition-all duration-300`}
            style={{ width: `${strengthPercentage}%` }}
          />
        </div>
      </div>

      {/* Requirements Checklist */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-1.5">
        <p className="text-xs font-semibold text-slate-700 mb-2">La password deve contenere:</p>
        
        <div className="space-y-1">
          <RequirementItem
            met={checks.length}
            text="Almeno 8 caratteri"
          />
          <RequirementItem
            met={checks.uppercase}
            text="Almeno 1 lettera maiuscola (A-Z)"
          />
          <RequirementItem
            met={checks.lowercase}
            text="Almeno 1 lettera minuscola (a-z)"
          />
          <RequirementItem
            met={checks.number}
            text="Almeno 1 numero (0-9)"
          />
          <RequirementItem
            met={checks.special}
            text="Almeno 1 carattere speciale (!@#$%^&*...)"
          />
        </div>
      </div>

      {/* Strength Tips */}
      {strength !== 'strong' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-blue-900">
              <p className="font-semibold mb-1">Suggerimento:</p>
              <p>
                {strength === 'weak' && 'Aggiungi lettere maiuscole, numeri e caratteri speciali per rendere la password più sicura.'}
                {strength === 'medium' && 'Quasi! Completa tutti i requisiti per una password forte.'}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const RequirementItem = ({ met, text }) => (
  <div className="flex items-center gap-2 text-xs">
    {met ? (
      <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
    ) : (
      <XCircle className="w-4 h-4 text-slate-400 flex-shrink-0" />
    )}
    <span className={met ? 'text-slate-700' : 'text-slate-500'}>
      {text}
    </span>
  </div>
);

export default PasswordStrengthIndicator;
